import React, { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import "../styles/Dashboard.css";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";
import PopupModal from "../components/PopupModal";
import pilotlyLogo from "../assets/Transparent svg/pilotly-logo.png";
import AddressAutocomplete from "../components/AddressAutocomplete";

import { loadGoogleMaps } from "../utils/loadGoogleMaps";


// ---- email utils (sanitize & migrate) ----
const normalizeEmail = (val) => {
  if (val == null) return null;
  try {
    // If someone stored JSON (array or object), coerce to string safely
    if (typeof val === "string" && val.trim().startsWith("[")) {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed) && parsed.length) return String(parsed[0]).trim().toLowerCase();
      return null;
    }
    if (Array.isArray(val)) return String(val[0] || "").trim().toLowerCase();
    return String(val).trim().toLowerCase();
  } catch {
    return String(val).trim().toLowerCase();
  }
};

const API_BASE = process.env.REACT_APP_API_BASE;
// const isGuest = localStorage.getItem("guest") === "true";


const getPageCount = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  return pdf.numPages;
};

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const createEmptyDispatchBlock = () => ({
  type: "",
  files: [],
  address: {
    name: "",
    phone: "",
    line1: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  },
  deliveryMethod: "",
  senderName: "",
  highlightSender: false,
  highlightRecipient: false,
  highlightPhone: false,
  highlightEmail: false,
  highlightAddress: false,
  highlightCity: false,
  highlightState: false,
  highlightFile: false,
});

const DELIVERY_PRICES = {
  USA: {
    usps_first_class_mail: 3,
    usps_priority_mail: 9,
    fedex_standard_overnight: 18,
    ups_2nd_day_air: 13,   // ✅ added
  },
  CANADA: {
    usps_first_class_mail_international: 4,
    usps_priority_mail_international: 12,
    fedex_international_priority: 20,
    ups_worldwide_saver: 15, // ✅ added
  },
};

const Dashboard = () => {
  const [dispatchBlocks, setDispatchBlocks] = useState([createEmptyDispatchBlock()]);
  const [noticeIndex, setNoticeIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [addressValid, setAddressValid] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    loadGoogleMaps();
  }, []);

  const [popup, setPopup] = useState({ show: false, title: "", message: "", type: "info", onConfirm: null });
  const [isProcessing, setIsProcessing] = useState(false);

  const [isGuest] = useState(localStorage.getItem("guest") === "true");
  const [guestEmail, setGuestEmail] = useState("");



  const token = localStorage.getItem("token");
  let userIsVerified = true;
  try {
    const decoded = jwtDecode(token);
    userIsVerified = decoded?.isVerified;
  } catch {
    userIsVerified = false;
  }

  const stateOptions = {
    USA: [..."AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(" ")],
    CANADA: [..."AB BC MB NB NL NS ON PE QC SK NT NU YT".split(" ")],
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setNoticeIndex((prev) => (prev + 1) % 4);
    }, 3500);
    return () => clearInterval(interval);
  }, []);


  const handleResend = async () => {
    try {
      const decoded = jwtDecode(token);
      const email = decoded?.email;
      if (!email) return alert("Invalid email.");

      const res = await axios.post(`${API_BASE}/api/auth/resend-verification`, { email });
      alert(res.data.message || "Verification email sent!");
    } catch {
      setPopup({
        show: true,
        title: "Invalid File Type",
        message: "Failed to resend verification.",
        type: "error",
      });

    }
  };

  useEffect(() => {
    const raw = localStorage.getItem("userEmail");
    if (!raw) return;
    const fixed = normalizeEmail(raw);
    if (fixed) localStorage.setItem("userEmail", fixed);
    else localStorage.removeItem("userEmail");
  }, []);

  const handleAddBlock = () => {
    setDispatchBlocks((prev) => [...prev, createEmptyDispatchBlock()]);
  };

  const handleRemoveBlock = (index) => {
    const updated = [...dispatchBlocks];
    updated.splice(index, 1);
    setDispatchBlocks(updated);
  };

  const handleBlockChange = (index, field, value) => {
    const updated = [...dispatchBlocks];

    // Reset highlight when user fixes an input
    if (field === "senderName") updated[index].highlightSender = false;
    if (field === "deliveryMethod") updated[index].highlightMethod = false;

    // 👇 if user changes type, reset files to avoid stuck validation
    if (field === "type") {
      updated[index].files = [];
    }

    updated[index][field] = value;
    setDispatchBlocks(updated);
  };

  const handleAddressChange = (index, field, value) => {
    const updated = [...dispatchBlocks];

    // Preserve existing country unless the user is explicitly changing it
    if (field === "country") {
      updated[index].address.country = value;
    } else {
      updated[index].address = {
        ...updated[index].address,
        [field]: value,
        country: updated[index].address.country || "USA",
      };
    }

    // Reset highlights as before
    if (field === "name") updated[index].highlightRecipient = false;
    if (field === "phone") updated[index].highlightPhone = false;
    if (field === "Email") updated[index].highlightEmail = false;
    if (field === "line1") updated[index].highlightAddress = false;
    if (field === "city") updated[index].highlightCity = false;
    if (field === "state") updated[index].highlightState = false;

    setDispatchBlocks(updated);
    verifyAddress(updated[index].address);
  };



  // const handleAddressChange = (index, field, value) => {
  //   const updated = [...dispatchBlocks];
  //   updated[index].address[field] = value;

  //   // Reset highlight dynamically when user types
  //   if (field === "name") updated[index].highlightRecipient = false;
  //   if (field === "phone") updated[index].highlightPhone = false;
  //   if (field === "line1") updated[index].highlightAddress = false;
  //   if (field === "city") updated[index].highlightCity = false;
  //   if (field === "state") updated[index].highlightState = false;

  //   setDispatchBlocks(updated);
  //   verifyAddress(updated[index].address);
  // };
  const handleFileUpload = async (blockIndex, filesArray, fileTypeOverride = null) => {
    setUploading(true);

    const blockType = fileTypeOverride || dispatchBlocks[blockIndex].type;

    if (!blockType) {
      setPopup({
        show: true,
        title: "Invalid File Type",
        message: "⚠️ Please choose a type (Check, Document, or Both) before uploading files.",
        type: "error",
      });

      setUploading(false);
      return;
    }

    const uploadedFiles = await Promise.all(
      filesArray.map(async (file) => {
        if (file.type !== "application/pdf") {
          setPopup({
            show: true,
            title: "Invalid File Type",
            message: "❌ Only PDF files are allowed.",
            type: "error",
          });

          return null;
        }

        // === NEW: Size limit (5MB) ===
        const MAX_SIZE = 5 * 1024 * 1024; // 5MB
        if (file.size > MAX_SIZE) {
          setPopup({
            show: true,
            title: "Invalid File Type",
            message: `❌ "${file.name}" is too large. Max allowed size is 5 MB.`,
            type: "error",
          });

          return null;
        }

        const existingNames = dispatchBlocks[blockIndex].files.map((f) => f.file.name);
        if (existingNames.includes(file.name)) {
          const proceed = window.confirm(
            `⚠️ The file "${file.name}" is already uploaded in this dispatch.\nDo you still want to keep it?`
          );
          if (!proceed) return null;
        }

        // === Optional: Duplicate check across all dispatch blocks ===
        const allExistingNames = dispatchBlocks.flatMap((block) => block.files.map((f) => f.file.name));
        if (allExistingNames.includes(file.name) && !existingNames.includes(file.name)) {
          const proceed = window.confirm(
            `⚠️ The file "${file.name}" is already used in another dispatch.\nDo you still want to use it here too?`
          );
          if (!proceed) return null;
        }

        try {
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          const pageCount = pdf.numPages;

          // === Validation Rules ===
          if (blockType === "check") {
            if (pageCount !== 1) {
              setPopup({
                show: true,
                title: "Invalid File Type",
                message: `❌ "${file.name}" has ${pageCount} pages. Checks must be exactly 1 page.`,
                type: "error",
              });

              return null;
            }
            const currentChecks = dispatchBlocks[blockIndex].files.filter((f) => f.pageCount === 1).length;
            if (currentChecks >= 3) {
              setPopup({
                show: true,
                title: "Invalid File Type",
                message: "❌ You can upload a maximum of 3 checks per dispatch.",
                type: "error",
              });

              return null;
            }
          }

          if (blockType === "document") {
            const currentDocs = dispatchBlocks[blockIndex].files.length;
            if (currentDocs >= 3) {
              setPopup({
                show: true,
                title: "Invalid File Type",
                message: "❌ You can upload a maximum of 3 documents per dispatch.",
                type: "error",
              });

              return null;
            }
            const currentTotalPages = dispatchBlocks[blockIndex].files.reduce((sum, f) => sum + f.pageCount, 0);
            if (currentTotalPages + pageCount > 15) {
              setPopup({
                show: true,
                title: "Invalid File Type",
                message: `❌ "${file.name}" has ${pageCount} pages. Adding it would exceed the 15-page limit (currently at ${currentTotalPages}).`,
                type: "error",
              });

              return null;
            }
            if (pageCount < 2) {
              setPopup({
                show: true,
                title: "Invalid File Type",
                message: `⚠️ "${file.name}" has only ${pageCount} page(s). Documents are usually longer. If this is a check, please upload under 'Check'.`,
                type: "error",
              });

            }
          }

          // === Add Prefix to File Name ===
          const prefixedName =
            blockType === "check" || blockType === "both-check"
              ? `micr-${file.name}`
              : blockType === "document" || blockType === "both-document"
                ? `docs-${file.name}`
                : file.name;

          // doc vs check tagging
          const isDoc =
            blockType === "document" ||
            blockType === "both-document" ||
            (blockType === "both" && pageCount > 1); // fallback: >1 page treat as doc

          return { file, pageCount, name: prefixedName, isDoc };
        } catch (err) {
          console.warn(`❌ Failed to read PDF: ${file.name}`, err);
          return null;
        }
      })
    );

    const validFiles = uploadedFiles.filter(Boolean);

    if (validFiles.length === 0) {
      setUploading(false);
      return;
    }

    setDispatchBlocks((prevBlocks) => {
      const updated = [...prevBlocks];

      if (blockType === "check") {
        const currentChecks = updated[blockIndex].files.filter((f) => f.pageCount === 1).length;
        const allowedSlots = Math.max(0, 3 - currentChecks);
        updated[blockIndex].files.push(...validFiles.slice(0, allowedSlots));
        if (validFiles.length > allowedSlots) {
          setPopup({
            show: true,
            title: "Invalid File Type",
            message: "⚠️ Only 3 checks allowed. Extra files were ignored.",
            type: "error",
          });

        }
      } else if (blockType === "document") {
        const currentDocs = updated[blockIndex].files.length;
        const allowedSlots = Math.max(0, 3 - currentDocs);
        updated[blockIndex].files.push(...validFiles.slice(0, allowedSlots));
        if (validFiles.length > allowedSlots) {
          setPopup({
            show: true,
            title: "Invalid File Type",
            message: "⚠️ Only 3 documents allowed. Extra files were ignored.",
            type: "error",
          });

        }
      } else {
        updated[blockIndex].files.push(...validFiles);
      }

      return updated;
    });

    setUploading(false);
  };

  const removeFile = (blockIndex, fileIndex) => {
    const updated = [...dispatchBlocks];
    updated[blockIndex].files.splice(fileIndex, 1);
    setDispatchBlocks(updated);
  };

  const verifyAddress = async (addr) => {
    if (!addr.line1 || !addr.city || !addr.state) return;
    const fullAddress = `${addr.line1}, ${addr.city}, ${addr.state}, ${addr.country}`;
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(fullAddress)}&format=json`;

    try {
      const response = await fetch(url, { headers: { "Accept-Language": "en" } });
      const data = await response.json();
      setAddressValid(data.length > 0);
    } catch {
      setAddressValid(false);
    }
  };

  const calculateTotal = () =>
    dispatchBlocks.reduce((total, block) => {
      let blockTotal = 0;

      // const country = dispatchBlocks[0]?.address?.country || "USA";

      const country = block.address?.country || "USA";

      const shippingFee = DELIVERY_PRICES[country]?.[block.deliveryMethod] || 0;

      if (block.type === "check") {
        const checkCount = block.files.length;
        if (checkCount > 0) {
          blockTotal += shippingFee;
          blockTotal += checkCount * 3;
        }
      } else if (block.type === "document") {
        const pageTotal = block.files.reduce((sum, f) => sum + f.pageCount, 0);
        if (pageTotal > 0) {
          blockTotal += shippingFee;
          blockTotal += pageTotal * 1;
        }
      } else if (block.type === "both") {
        const checkCount = block.files.filter((f) => f.pageCount === 1 && f.isDoc === false).length;
        const pageTotal = block.files
          .filter((f) => f.isDoc === true)
          .reduce((sum, f) => sum + f.pageCount, 0);

        if (checkCount > 0 || pageTotal > 0) {
          blockTotal += shippingFee;
        }

        blockTotal += checkCount * 3;
        blockTotal += pageTotal * 1;
      }
      return total + blockTotal;
    }, 0);

  const isValidPostal = (zip, country) => {
    if (country === "CANADA") {
      return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(zip.trim());
    }
    if (country === "USA") {
      return /^\d{5}(-\d{4})?$/.test(zip.trim());
    }
    return true;
  };

  const allHaveMethod = dispatchBlocks.every((b) => !!b.deliveryMethod);




  const handleNext = () => {
    const token = localStorage.getItem("token");
    const isGuest = localStorage.getItem("guest") === "true";

    // 1) Resolve email based on mode
    let email = null;

    if (isGuest) {
      email = normalizeEmail(guestEmail);
      if (!email) {
        setPopup({
          show: true,
          title: "Missing Email",
          message: "⚠️ Please enter a valid email for guest dispatch.",
          type: "error",
        });
        return;
      }
    } else {
      // logged-in path
      let decodedEmail = null;
      try {
        const decoded = jwtDecode(token);
        decodedEmail = decoded?.email || null;
      } catch { }

      email =
        normalizeEmail(decodedEmail) ||
        normalizeEmail(localStorage.getItem("userEmail")) ||
        (() => {
          try {
            const savedUser = JSON.parse(localStorage.getItem("user"));
            return normalizeEmail(savedUser?.email || null);
          } catch {
            return null;
          }
        })();

      if (!email) {
        // Fallback (string, never an array)
        email = "no-email@cjetdispatch.com";
        console.warn("⚠️ No valid user email found, using fallback dummy");
      }
    }

    // 2) Persist a clean, single string (don’t keep arrays around)
    if (email) localStorage.setItem("userEmail", email);
    else localStorage.removeItem("userEmail");

    // --- everything else below is your original validation & navigation ---
    const totalAmount = calculateTotal();
    const mailDate = new Date().toISOString().split("T")[0];
    const country = dispatchBlocks[0]?.address?.country || "USA";

    const updatedBlocks = [...dispatchBlocks];

    const findAndHighlight = (condition, key, message, title = "Validation Error") => {
      const index = updatedBlocks.findIndex(condition);
      if (index !== -1) {
        updatedBlocks[index][key] = true;
        setDispatchBlocks(updatedBlocks);
        setPopup({ show: true, title, message, type: "error" });
        return true;
      }
      return false;
    };

    if (findAndHighlight((b) => !b.senderName?.trim(), "highlightSender", "⚠️ Please fill in the Sender Name.", "Missing Sender Name")) return;
    if (findAndHighlight((b) => !b.address.name?.trim(), "highlightRecipient", "⚠️ Please fill in the Recipient Name.", "Missing Recipient Name")) return;
    if (findAndHighlight((b) => !b.address.phone?.trim(), "highlightPhone", "⚠️ Please fill in the Mobile Number.", "Missing Mobile Number")) return;
    // If you truly require sender email per-block, implement a proper field; for now we skip the mistaken check.
    // if (findAndHighlight((b) => !b.senderEmail?.trim(), "highlightEmail", "Missing Sender Email")) return;
    if (findAndHighlight((b) => !b.address.line1?.trim(), "highlightAddress", "⚠️ Please fill in the Address Line 1.", "Missing Address")) return;
    if (findAndHighlight((b) => !b.address.city?.trim(), "highlightCity", "⚠️ Please fill in the City field.", "Missing City")) return;
    if (findAndHighlight((b) => !b.address.state?.trim(), "highlightState", "⚠️ Please select a State.", "Missing State")) return;
    if (findAndHighlight((b) => b.files.length === 0, "highlightFile", "⚠️ Please upload at least one file.", "Missing File Upload")) return;

    for (const block of updatedBlocks) {
      if (!isValidPostal(block.address.zip, block.address.country)) {
        setPopup({
          show: true,
          title: "Invalid Postal Code",
          message: `❌ Invalid postal code format for ${block.address.country}. Please correct it.`,
          type: "error",
        });
        return;
      }
    }

    const resetBlocks = updatedBlocks.map((b) => ({
      ...b,
      highlightSender: false,
      highlightRecipient: false,
      highlightPhone: false,
      highlightEmail: false,
      highlightAddress: false,
      highlightCity: false,
      highlightState: false,
      highlightFile: false,
    }));
    setDispatchBlocks(resetBlocks);

    setIsProcessing(true);
    setTimeout(() => {
      navigate("/payment", {
        state: {
          dispatchBlocks,
          guestEmail,              // keep for UI, but…
          totalAmount,
          email,                   // ← use the sanitized single-string email
          mailDate,
          country,
          dispatchResponses: [],
          baseCurrency: "USD",
        },
      });
    }, 2000);
  };

  // const handleNext = () => {
  //   const token = localStorage.getItem("token");
  //   let decodedEmail = null;

  //   try {
  //     const decoded = jwtDecode(token);
  //     decodedEmail = decoded?.email || null;
  //   } catch (err) {
  //     console.warn("JWT decode failed:", err.message);
  //   }

  //   let userEmail = decodedEmail || localStorage.getItem("userEmail") || null;

  //   if (!userEmail) {
  //     try {
  //       const savedUser = JSON.parse(localStorage.getItem("user"));
  //       userEmail = savedUser?.email || null;
  //     } catch { }
  //   }

  //   if (!userEmail) {
  //     userEmail = "no-email@cjetdispatch.com";
  //     console.warn("⚠️ No valid user email found, using fallback dummy");
  //   }

  //   localStorage.setItem("userEmail", userEmail);

  //   const totalAmount = calculateTotal();
  //   const mailDate = new Date().toISOString().split("T")[0];
  //   const country = dispatchBlocks[0]?.address?.country || "USA";

  //   // Validate and highlight fields
  //   const updatedBlocks = [...dispatchBlocks];

  //   const findAndHighlight = (condition, key, message, title) => {
  //     const index = updatedBlocks.findIndex(condition);
  //     if (index !== -1) {
  //       updatedBlocks[index][key] = true;
  //       setDispatchBlocks(updatedBlocks);
  //       setPopup({
  //         show: true,
  //         title,
  //         message,
  //         type: "error",
  //       });
  //       return true;
  //     }
  //     return false;
  //   };

  //   if (findAndHighlight((b) => !b.senderName?.trim(), "highlightSender", "⚠️ Please fill in the Sender Name.", "Missing Sender Name")) return;
  //   if (findAndHighlight((b) => !b.address.name?.trim(), "highlightRecipient", "⚠️ Please fill in the Recipient Name.", "Missing Recipient Name")) return;
  //   if (findAndHighlight((b) => !b.address.phone?.trim(), "highlightPhone", "⚠️ Please fill in the Mobile Number.", "Missing Mobile Number")) return;
  //   if (findAndHighlight((b) => !b.senderName?.trim(), "highlightEmail", "Missing Sender Email")) return;
  //   if (findAndHighlight((b) => !b.address.line1?.trim(), "highlightAddress", "⚠️ Please fill in the Address Line 1.", "Missing Address")) return;
  //   if (findAndHighlight((b) => !b.address.city?.trim(), "highlightCity", "⚠️ Please fill in the City field.", "Missing City")) return;
  //   if (findAndHighlight((b) => !b.address.state?.trim(), "highlightState", "⚠️ Please select a State.", "Missing State")) return;
  //   if (findAndHighlight((b) => b.files.length === 0, "highlightFile", "⚠️ Please upload at least one file.", "Missing File Upload")) return;

  //   // Postal code validation
  //   for (const block of updatedBlocks) {
  //     if (!isValidPostal(block.address.zip, block.address.country)) {
  //       setPopup({
  //         show: true,
  //         title: "Invalid Postal Code",
  //         message: `❌ Invalid postal code format for ${block.address.country}. Please correct it.`,
  //         type: "error",
  //       });
  //       return;
  //     }
  //   }

  //   // Reset highlights before moving forward
  //   const resetBlocks = updatedBlocks.map((b) => ({
  //     ...b,
  //     highlightSender: false,
  //     highlightRecipient: false,
  //     highlightPhone: false,
  //     highlightEmail: false,
  //     highlightAddress: false,
  //     highlightCity: false,
  //     highlightState: false,
  //     highlightFile: false,

  //   }));
  //   setDispatchBlocks(resetBlocks);

  //   // Proceed to payment
  //   setIsProcessing(true);
  //   setTimeout(() => {
  //     navigate("/payment", {
  //       state: {
  //         dispatchBlocks,
  //         guestEmail,
  //         totalAmount,
  //         email: userEmail,
  //         mailDate,
  //         country,
  //         dispatchResponses: [],
  //         baseCurrency: "USD",
  //       },
  //     });
  //   }, 2000);
  // };






  return (
    <DashboardLayout>
      {isGuest && (
        <div
          style={{
            background: "#fff8e1",
            color: "#8a6d3b",
            padding: "12px",
            borderRadius: "6px",
            marginBottom: "16px",
            border: "1px solid #ffecb5",
          }}
        >
          ⚠️ You’re using Guest Mode — Sign up or log in to save your dispatch
          history.
        </div>
      )}
      {!userIsVerified && localStorage.getItem("isGoogleUser") !== "true" && (
        <div className="verify-banner">
          <p>📧 Please verify your email to access all features.</p>
          <button onClick={handleResend}>Resend Verification</button>
        </div>
      )}

      <div className="dashboard-container">
        <h2 className="dashboard-title">Upload Your Dispatches</h2>
        <p className="dashboard-subtext">
          You can send multiple dispatches with different types, addresses, and files.
        </p>

        {dispatchBlocks.map((block, index) => (
          <div key={index} className="dispatch-block">
            <h3>Dispatch #{index + 1}</h3>

            {dispatchBlocks.length > 1 && (
              <button
                className="remove-dispatch-button"
                onClick={() => handleRemoveBlock(index)}
                title="Remove this dispatch"
              >
                ❌
              </button>
            )}

            <div className="sliding-notice">
              <span className="notice-message">
                {[
                  "📄 Documents are charged $1 per page.",
                  "💵 Checks cost $3 per file.",
                  "📦 'Both' combines check + docs in one envelope.",
                  "📂 You can upload multiple dispatch blocks.",
                ][noticeIndex]}
              </span>
            </div>

            {/* Country Buttons */}
            <div className="country-buttons">
              {["USA", "CANADA"].map((country) => (
                <button
                  key={country}
                  className={`country-button ${block.address.country === country ? "active" : ""}`}
                  onClick={() => handleAddressChange(index, "country", country)}
                >
                  {country}
                </button>
              ))}
            </div>

            {/* Type + Delivery */}
            <div className="type-method-row">
              <div className="select-wrapper">
                <select
                  value={block.type}
                  onChange={(e) => handleBlockChange(index, "type", e.target.value)}
                  disabled={isProcessing}
                >
                  <option value="">Choose Type</option>
                  <option
                    value="check"
                    disabled={isGuest}
                    style={{ color: isGuest ? "#aaa" : "#000" }}
                  >
                    Check {isGuest ? "— (Login required)" : ""}
                  </option>
                  <option value="document">Document</option>
                  <option
                    value="both"
                    disabled={isGuest}
                    style={{ color: isGuest ? "#aaa" : "#000" }}
                  >
                    Both in One Envelope {isGuest ? "— (Login required)" : ""}
                  </option>
                </select>

                {/* <select
                  value={block.type}
                  onChange={(e) => handleBlockChange(index, "type", e.target.value)}
                >
                  <option value="">Choose Type</option>
                  <option value="check">Check</option>
                  <option value="document">Document</option>
                  <option value="both">Both in One Envelope</option>
                </select> */}
              </div>

              <div className="select-wrapper">
                <select
                  value={block.deliveryMethod}
                  onChange={(e) => handleBlockChange(index, "deliveryMethod", e.target.value)}
                >
                  <option value="">Choose Delivery Type</option>
                  {block.address.country === "CANADA" ? (
                    <>
                      <option value="usps_first_class_mail_international">USPS First Class Intl</option>
                      <option value="usps_priority_mail_international">USPS Priority Intl</option>
                      <option value="fedex_international_priority">FedEx Intl Priority</option>
                      <option value="ups_worldwide_saver">UPS Worldwide Saver</option>
                    </>
                  ) : (
                    <>
                      <option value="usps_first_class_mail">USPS First Class</option>
                      <option value="usps_priority_mail">USPS Priority</option>
                      <option value="fedex_standard_overnight">FedEx Overnight</option>
                      <option value="ups_2nd_day_air">UPS 2nd Day Air</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* Address + Sender Info */}
            <input
              placeholder="Sender Name"
              value={block.senderName}
              onChange={(e) => handleBlockChange(index, "senderName", e.target.value)}
              className={`full-width-input ${block.highlightSender ? "input-error" : ""}`}
            />

            <input
              placeholder="Recipient Name"
              value={block.address.name}
              onChange={(e) => handleAddressChange(index, "name", e.target.value)}
              className={`full-width-input ${block.highlightRecipient ? "input-error" : ""}`}
            />

            <div className="row-input-pair">
              <input
                placeholder="Mobile"
                value={block.address.phone}
                onChange={(e) => handleAddressChange(index, "phone", e.target.value)}
                className={`styled-input ${block.highlightPhone ? "input-error" : ""}`}
              />
              {isGuest && (
                <div className="guest-email-section" style={{ marginTop: "10px" }}>
                  <input
                    type="email"
                    id="guestEmail"
                    placeholder="guest@example.com"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    required
                    className={`styled-input ${block.highlightEmail ? "input-error" : ""}`}
                  />
                </div>
              )}

              <AddressAutocomplete
                value={block.address.line1 || ""}
                onSelect={(addr) => {
                  // ✅ Google suggestion picked
                  handleAddressChange(index, "line1", addr.line1);
                  handleAddressChange(index, "city", addr.city);
                  handleAddressChange(index, "state", addr.state);
                  handleAddressChange(index, "zip", addr.zip);

                  // ✅ Keep current selected country if user already chose one
                  if (!block.address.country) {
                    handleAddressChange(index, "country", addr.country);
                  }
                }}
                onManualInput={(text) => {
                  // ✅ User typing manually: only update address line 1, leave others untouched
                  handleAddressChange(index, "line1", text);
                }}
                placeholder="Address Line 1"
                className={`styled-input ${block.highlightAddress ? "input-error" : ""}`}
                country={block.address.country}
              />
            </div>

            {/* <div className="row-input-pair">
              <input
                placeholder="Mobile"
                value={block.address.phone}
                onChange={(e) => handleAddressChange(index, "phone", e.target.value)}
                className={`styled-input ${block.highlightPhone ? "input-error" : ""}`}
              />
              
              <input
                placeholder="Address Line 1"
                value={block.address.line1}
                onChange={(e) => handleAddressChange(index, "line1", e.target.value)}
                className={`styled-input ${block.highlightAddress ? "input-error" : ""}`}
              />
            </div> */}

            <div className="row-triple-input">
              <input
                placeholder="City"
                value={block.address.city}
                onChange={(e) => handleAddressChange(index, "city", e.target.value)}
                className={`triple-input ${block.highlightCity ? "input-error" : ""}`}
              />
              <div className="select-wrapper-sm">
                <select
                  value={block.address.state}
                  onChange={(e) => handleAddressChange(index, "state", e.target.value)}
                >
                  <option value="">State</option>
                  {(stateOptions[block.address.country] || []).map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
              <input
                placeholder="Zip Code"
                value={block.address.zip}
                onChange={(e) => handleAddressChange(index, "zip", e.target.value)}
                className="triple-input"
              />
            </div>

            {/* File Upload Section */}
            {block.type === "check" && (
              <div className="upload-wrapper">
                <p>Upload Check (1 page only, $3)</p>
                <label className="upload-box">
                  <input
                    type="file"
                    accept="application/pdf"
                    hidden
                    onChange={(e) => {
                      handleFileUpload(index, Array.from(e.target.files), "check");
                      e.target.value = "";
                    }}
                  />
                  <div className="upload-content">
                    <span className="plus-icon">+</span>
                    <span className="upload-text">Choose Check</span>
                  </div>
                </label>
              </div>
            )}

            {block.type === "document" && (
              <div className="upload-wrapper">
                <p>Upload Documents ($1 per page)</p>
                <label className="upload-box">
                  <input
                    type="file"
                    accept="application/pdf"
                    multiple
                    hidden
                    onChange={(e) => {
                      handleFileUpload(index, Array.from(e.target.files), "document");
                      e.target.value = "";
                    }}
                  />
                  <div className="upload-content">
                    <span className="plus-icon">+</span>
                    <span className="upload-text">Choose Document(s)</span>
                  </div>
                </label>
              </div>
            )}

            {block.type === "both" && (
              <div className="upload-row">
                <div className="upload-wrapper">
                  <label className="upload-box">
                    <input
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={(e) => {
                        handleFileUpload(index, Array.from(e.target.files), "both-check");
                        e.target.value = "";
                      }}
                    />
                    <div className="upload-content">
                      <span className="plus-icon">+</span>
                      <span className="upload-text">Choose Check</span>
                    </div>
                  </label>
                  <p className="upload-caption">Upload Check (1 page only, $3)</p>
                </div>

                <div className="upload-wrapper">
                  <label className="upload-box">
                    <input
                      type="file"
                      accept="application/pdf"
                      multiple
                      hidden
                      onChange={(e) => {
                        handleFileUpload(index, Array.from(e.target.files), "both-document");
                        e.target.value = "";
                      }}
                    />
                    <div className="upload-content">
                      <span className="plus-icon">+</span>
                      <span className="upload-text">Choose Document(s)</span>
                    </div>
                  </label>
                  <p className="upload-caption">Upload Document(s) ($1 per page)</p>
                </div>
              </div>
            )}

            {/* File List */}
            {block.files.length > 0 && (
              <>
                <ul>
                  {block.files.map((f, i) => (
                    <li key={i}>
                      {f.file.name} ({f.pageCount} pages)
                      <button onClick={() => removeFile(index, i)}>Remove</button>
                    </li>
                  ))}
                </ul>
                <p className="subtotal">
                  Subtotal: ${(() => {
                    let blockTotal = 0;
                    const country = block.address.country || "USA";
                    const shippingFee = DELIVERY_PRICES[country]?.[block.deliveryMethod] || 0;

                    if (block.type === "check") {
                      blockTotal += block.files.length * 3 + shippingFee;
                    } else if (block.type === "document") {
                      const pages = block.files.reduce((sum, f) => sum + f.pageCount, 0);
                      blockTotal += pages * 1 + shippingFee;
                    } else if (block.type === "both") {
                      const checks = block.files.filter((f) => !f.isDoc).length;
                      const docs = block.files.filter((f) => f.isDoc).reduce((s, f) => s + f.pageCount, 0);
                      blockTotal += checks * 3 + docs * 1 + shippingFee;
                    }
                    return blockTotal;
                  })()}
                </p>
              </>
            )}
          </div>
        ))}

        <div className="button-row">
          <button onClick={handleAddBlock} className="add-dispatch-button">
            + Add Another Dispatch
          </button>
        </div>

        <div className="pay-now-container">
          <button className="next-button" onClick={handleNext} disabled={uploading || !allHaveMethod}>
            {uploading ? "Processing..." : `Pay Now – $${calculateTotal()}`}
          </button>
        </div>
      </div>

      {/* Popup + Loading Overlay */}
      {popup.show && (
        <PopupModal
          title={popup.title}
          message={popup.message}
          type={popup.type}
          onClose={() => setPopup({ ...popup, show: false })}
          onConfirm={popup.onConfirm}
        />
      )}

      {isProcessing && (
        <div className="processing-overlay">
          <div className="processing-box">
            <img src={pilotlyLogo} alt="Pilotlly Logo" className="processing-logo" />
            <p className="processing-text">Preparing your dispatch summary...</p>
            <p className="powered-text">
              Dispatch powered by <strong>Pilotlly</strong>
            </p>
            <div className="spinner"></div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Dashboard;